import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export type ToastInput = {
  title?: string;
  message: string;
  type?: ToastType;
  durationMs?: number;
};

type ToastItem = {
  id: string;
  title: string;
  message: string;
  type: ToastType;
  durationMs: number;
};

type ToastContextValue = {
  toast: (input: ToastInput | string) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_TITLES: Record<ToastType, string> = {
  success: 'Saved successfully',
  error: 'Something went wrong',
  info: 'Notice',
};

function normalize(input: ToastInput | string): Omit<ToastItem, 'id'> {
  if (typeof input === 'string') {
    return {
      title: DEFAULT_TITLES.success,
      message: input,
      type: 'success',
      durationMs: 3200,
    };
  }
  const type = input.type || 'success';
  return {
    title: input.title || DEFAULT_TITLES[type],
    message: input.message,
    type,
    durationMs: input.durationMs ?? (type === 'error' ? 4500 : 3200),
  };
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Record<string, number>>({});

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current[id];
    if (timer) {
      window.clearTimeout(timer);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback(
    (input: ToastInput | string) => {
      const item = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ...normalize(input) };
      setToasts((prev) => [...prev.slice(-4), item]);
      timers.current[item.id] = window.setTimeout(() => dismiss(item.id), item.durationMs);
    },
    [dismiss]
  );

  useEffect(() => {
    return () => {
      Object.values(timers.current).forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => {
    const api: ToastContextValue = {
      toast: push,
      success: (message, title) => push({ type: 'success', message, title: title || 'Saved successfully' }),
      error: (message, title) => push({ type: 'error', message, title: title || 'Action failed' }),
      info: (message, title) => push({ type: 'info', message, title: title || 'Notice' }),
    };
    return api;
  }, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 w-[min(380px,calc(100vw-2rem))] pointer-events-none"
        aria-live="polite"
        aria-relevant="additions"
      >
        {toasts.map((t) => {
          const tone =
            t.type === 'error'
              ? 'border-red-200 bg-white'
              : t.type === 'info'
                ? 'border-sky-200 bg-white'
                : 'border-emerald-200 bg-white';
          const iconWrap =
            t.type === 'error' ? 'bg-red-500' : t.type === 'info' ? 'bg-sky-500' : 'bg-emerald-500';
          const Icon = t.type === 'error' ? AlertCircle : t.type === 'info' ? Info : CheckCircle;
          return (
            <div
              key={t.id}
              className={`pointer-events-auto flex items-start gap-3 rounded-xl border shadow-lg px-3 py-2.5 ${tone}`}
              role="status"
            >
              <div className={`mt-0.5 p-1 rounded-full text-white shrink-0 ${iconWrap}`}>
                <Icon size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800">{t.title}</p>
                <p className="text-xs text-slate-600 break-words">{t.message}</p>
              </div>
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600 p-0.5"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe no-op fallback so components don't crash outside provider during tests.
    const noop = () => undefined;
    return { toast: noop, success: noop, error: noop, info: noop };
  }
  return ctx;
}
